import {
  ConsumptionStorageType,
  UsageGroupByType,
} from "@shared/types/localTypes/usage-explorer.ts";
import { ConsumptionCsvGroupBy } from "@shared/services/usage-explorer/fetchConsumptionCsv.ts";
import { TourConsumptionMember } from "@shared/types/localTypes/tour.ts";
import json from "../../json/usageExplorer/usageExplorerConsumption.json";

type TemplateMember = {
  readonly name: string;
  readonly trafficGb: number;
  readonly artifactStorageGb: number;
  readonly normalizedStorageGb: number;
};

type TemplateJpd = {
  readonly jpd: string;
  readonly projects: ReadonlyArray<TemplateMember>;
  readonly repositories: ReadonlyArray<TemplateMember>;
};

type Template = { readonly jpds: ReadonlyArray<TemplateJpd> };

type StorageField = Exclude<keyof TemplateMember, "name" | "trafficGb">;

type TourConsumptionMembers = (args: {
  readonly groupBy: ConsumptionCsvGroupBy;
  readonly storageType: ConsumptionStorageType;
}) => ReadonlyArray<TourConsumptionMember>;

const TEMPLATE = json satisfies Template;

const STORAGE_FIELD_BY_STORAGE_TYPE = {
  binary: "normalizedStorageGb",
  artifact: "artifactStorageGb",
  normalized: "normalizedStorageGb",
} as const satisfies Record<ConsumptionStorageType, StorageField>;

const tourConsumptionMembers: TourConsumptionMembers = ({
  groupBy,
  storageType,
}) => {
  const storageField = STORAGE_FIELD_BY_STORAGE_TYPE[storageType];
  const toMember = (
    jpd: string,
    member: TemplateMember,
  ): TourConsumptionMember => ({
    jpd,
    name: member.name,
    trafficGb: member.trafficGb,
    storageGb: member[storageField],
  });

  switch (groupBy) {
    case UsageGroupByType.PROJECT:
      return TEMPLATE.jpds.flatMap(({ jpd, projects }) =>
        projects.map((member) => toMember(jpd, member)),
      );
    case UsageGroupByType.REPOSITORY:
      return TEMPLATE.jpds.flatMap(({ jpd, repositories }) =>
        repositories.map((member) => toMember(jpd, member)),
      );
    case UsageGroupByType.TECHNICAL_SERVER_NAME:
      return TEMPLATE.jpds.map(({ jpd, projects }) => ({
        jpd,
        name: jpd,
        trafficGb: projects.reduce((sum, member) => sum + member.trafficGb, 0),
        storageGb: projects.reduce(
          (sum, member) => sum + member[storageField],
          0,
        ),
      }));
  }
};

export default tourConsumptionMembers;
